import { Trash } from '@griply/assets';
import { getLocalizedStringValue } from '@griply/utils';
import { useArchiveLifeArea } from 'features/life-areas';
import { useFavoriteIds, useToggleFavorite } from 'features/user';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArchiveDocumentsBoxBig16,
  PenEdit16,
  Star16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenDeleteLifeAreaDialog } from 'shared/contexts/delete-life-area';
import { useOpenEditLifeAreaDialog } from 'shared/contexts/life-area-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useUser } from 'shared/hooks/use-user';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { LifeArea } from 'shared/types/life-area';
import { LimitKeys } from 'shared/types/limit-keys';
import { FavoriteType } from 'shared/types/user-settings';

export type LifeAreaContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref'
> & {
  lifeArea: LifeArea;
  onClose: () => void;
};

export const LifeAreaContextMenu: React.FC<LifeAreaContextMenuProps> = ({
  lifeArea,
  onClose,
  location,
  position,
  referenceElement,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const user = useUser();
  const favoriteIds = useFavoriteIds();
  const isFavorite = useMemo(
    () => favoriteIds.includes(lifeArea.id),
    [favoriteIds, lifeArea.id],
  );

  const openEditDialog = useOpenEditLifeAreaDialog();
  const toggleFavorite = useToggleFavorite();
  const openDeleteDialog = useOpenDeleteLifeAreaDialog();
  const archiveLifeArea = useArchiveLifeArea();

  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: editLifeAreasAllowed } = usePremiumFeatureAllowed(
    LimitKeys.EditLifeAreas,
    { user },
  );

  const onEdit = () => {
    if (!editLifeAreasAllowed) {
      openPremiumDialog();
      onClose();
      return;
    }

    openEditDialog(
      {
        ...lifeArea,
        name: getLocalizedStringValue(lifeArea.name, locale),
      },
      lifeArea.id,
    );
    onClose();
  };

  const onArchive = () => {
    archiveLifeArea(lifeArea);
    onClose();
  };

  const onFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    toggleFavorite(lifeArea.id, FavoriteType.LifeArea);
    onClose();
  };

  const onDelete = () => {
    if (!editLifeAreasAllowed) {
      openPremiumDialog();
      onClose();
      return;
    }

    openDeleteDialog(lifeArea);
    onClose();
  };

  return (
    <PopupMenu
      ref={containerRef}
      location={location}
      position={position}
      referenceElement={referenceElement}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        {!lifeArea.archivedAt && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onEdit}
              start={<Icon icon={PenEdit16} />}
              isPremium={!editLifeAreasAllowed}
            >
              {t('context-menus.life-area.labels.edit')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        <PopupMenuListItem>
          <PopupMenuButton onClick={onFavorite} start={<Icon icon={Star16} />}>
            {t(
              isFavorite
                ? 'context-menus.goal.labels.remove-favorite'
                : 'context-menus.goal.labels.add-favorite',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onArchive}
            start={<Icon icon={ArchiveDocumentsBoxBig16} />}
          >
            {t(
              !!lifeArea.archivedAt
                ? 'context-menus.life-area.labels.de-archive'
                : 'context-menus.life-area.labels.archive',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDelete}
            start={<Icon icon={Trash} />}
            startColor={Colors.Error}
            textColor={Colors.Error}
            isPremium={!editLifeAreasAllowed}
          >
            {t('context-menus.life-area.labels.delete')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
